import { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { ISideTreeEntry, IStore } from '../../interfaces';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ALL_COUNTRIES_NAME, COMPETITION_LINK, MATCH_LINK, MATCH_NAME, REAL_MADRID, REAL_MADRID_NAME, SELECT_BY_COUNTRIES } from '../constants';
import * as actions from '../../actions';
import { Localizer } from '../../utils/localizer';
import { Competitions } from '../../utils/competitions';
import { useClickOutside } from '../../utils/useClickOutside';
import { useAppDispatch } from '../../utils/modules';
import styles from './SideTree.module.css';

//#region styled Components

export const StyledAnimatedOnHover = styled.div`
	transition: color, background-color;
	transition-property: color, background-color;
	transition-duration: 0.4s;
	transition-timing-function: ease-out;
	border-radius: 4px;
`;

export const LoadingIconContainer = styled.div`
	margin-top: 50px;
`;

export const LoadingIcon = styled.div`
	border: 16px solid transparent;
	border-top: 16px solid #F89504;
	border-radius: 50%;
	width: 120px;
	height: 120px;
	animation: spin 2s linear infinite;
	margin: auto;
`;

export const LoadingIconDesc = styled.div`
	font-sze: 18px;
	margin-top: 25px;
	text-align: center;
	font-family: 'Europa-big';
`;

//#endregion

export default function SideTree() {

	const dispatch = useAppDispatch();

	const containerRef = useRef<HTMLDivElement>(null);
	
	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return !!completeState?.ui?.isMobileViewActive;
		});

	const showTreeMap = useSelector(
		(completeState: IStore): boolean => {
			return !!completeState?.ui?.showTreeMap;
		});

	const entries = useSelector(
		(completeState: IStore): ISideTreeEntry[] => {
			return completeState?.sideTree?.sideTreeEntries || [];
		});

	const closeTreeMapIcon = useCallback(
		() => {
			dispatch(actions.hideTreeMap());
		},
		[dispatch]
	);

	const handleClickOutside = useCallback(
		() => {
			if (isMobileViewActive && showTreeMap) {
				dispatch(actions.hideTreeMap());
			}
		},
		[dispatch, isMobileViewActive, showTreeMap]
	);
	useClickOutside(containerRef, handleClickOutside);

	const renderLink = useCallback(
		(sideEntry: ISideTreeEntry) => {
			switch (sideEntry.countryCode) {
				case MATCH_NAME:
					return (
						<Link to={`/${MATCH_LINK}`}>
							{`${sideEntry.name} (${sideEntry.totalNumber})`}
						</Link>
					);
				case REAL_MADRID:
					return (
						<Link to={`/${REAL_MADRID}`}>
							{`${REAL_MADRID_NAME} (${sideEntry.totalNumber})`}
						</Link>
					);
				case SELECT_BY_COUNTRIES:
					return (
						<Link to={`/${SELECT_BY_COUNTRIES}`}>
							{`${sideEntry.name}`}
						</Link>
					);
				case COMPETITION_LINK:
					return (
						<Link to={`/${COMPETITION_LINK}`}>
							{`${sideEntry.name}`}
						</Link>
					);
				default:
					return (
						<Link to={`/${Localizer.getLocalizedCountryName(sideEntry.countryCode)}`}>
							{sideEntry.name !== ALL_COUNTRIES_NAME && <img className={styles.flagImage}
								src={Competitions.getCompetitionRegionImagePath(sideEntry.countryCode)}
							/>}
							{`${sideEntry.name} ${sideEntry.name !== ALL_COUNTRIES_NAME && sideEntry.totalNumber > 0
								? `(${sideEntry.totalTeamsNumber}${sideEntry.countryCode === ALL_COUNTRIES_NAME ? ' CLUBS' : ''})` : ''}`}
						</Link>
					);
			}
		},
		[]
	);

	return (
		<div className={isMobileViewActive ? styles.containerMobile : styles.containerDesktop}ref={containerRef}>
			{
				isMobileViewActive &&
					<div className={styles.rightHeader} onClick={closeTreeMapIcon}>
						<i className='fa fa-window-close'/>	
					</div>
			}
			{
				entries.length <= 2
					? <div className='loadingIconContainer'>
						<div className='loadingIcon'/>
						<div className='loadingIconDesc'>{'LOADING SCARVES...'}</div>
					</div>
					: entries.map((x, i) => {
						return (
							<div className={styles.styledLink} key={i} style={{ marginBottom: i === 2 || i === 8 ? 15 : 0 }}>
								{renderLink(x)}
							</div>
						);
					})
			}
		</div>
	);
}
